import { CoreCard, CoreStepper } from "@verity/components/core";
import styles from "./index.module.css";
import { ScreenReaderText, VerityAssessmentPageName } from "@verity/components";
import { useCallback, useContext, useMemo, useState } from "react";
import i18n from "../../../../i18n";
import AssessmentTypeStep from "./AssessmentTypeStep";
import { baseTransKey } from "./verityAssessmentHelper";
import { CreateAssessmentFooter } from "./shared";
import { PortalControllerContext } from "../../../context";
import { CourseGradeBlock, GeneralInfoBlock, SubmissionBlock, TimingBlock } from "./AssessmentDetailsBlock";
import EditAssessmentOptionsPage from "../editAssessmentOptionPage";
import { VerityAssessmentType } from "@verity/database/prisma";
import QuestionsStep from "./QuestionsStep";
import InstructionStep from "./InstructionStep";
import ReviewStep from "./ReviewStep";
import { VerityAssessment } from "@verity/api-client/types";
import { QuizWithProctoringOptions } from "../assessments-page-helper";
import useFetchVerityAssessmentInfo from "./hooks/useFetchVerityAssessmentInfo";
import useInitialValues from "./hooks/useInitialValues";
import useAssessmentDetails from "./hooks/useAssessmentDetails";
import useAddInstructions from "./hooks/useAddInstructions";
import useEditQuizStep from "./hooks/useEditQuizStep";
import useQuestionDelivery from "./hooks/useQuestionDelivery";
import useQuizQuestions from "./hooks/useQuizQuestions";
import useVerityAssessmentFlow from "./hooks/useVerityAssessmentFlow";

export type VerityAssessmentSectionProps = {
	onFinish: () => void;
	verityAssessment?: VerityAssessment;
	assessment?: QuizWithProctoringOptions;
};

export default function VerityAssessmentSection({
	onFinish,
	verityAssessment,
	assessment,
}: VerityAssessmentSectionProps) {
	const { portalContext } = useContext(PortalControllerContext);
	const { lms } = portalContext;

	const isEditing = useMemo(() => Boolean(verityAssessment), [verityAssessment]);
	const [assessmentType, setAssessmentType] = useState<VerityAssessmentType>(verityAssessment?.type ?? "quiz");

	const fetchConfig = useFetchVerityAssessmentInfo(assessment?.id);
	const { courses, loading, applications, extensions, modules, fetchingModules, fetchCourseModules, instructorFiles } =
		fetchConfig;

	const initialValues = useInitialValues(verityAssessment, assessment, instructorFiles);

	const assessmentDetails = useAssessmentDetails(initialValues.assessmentDetails);
	const addInstructions = useAddInstructions(initialValues.addInstructions);
	const questionDelivery = useQuestionDelivery(initialValues.questionDelivery);
	const editQuizConfig = useEditQuizStep();
	const quizQuestions = useQuizQuestions(initialValues.quizQuestions);
	const { questions } = quizQuestions;

	const { editQuizRef, editQuizWrapperProps, enforcedProfilesOnly } = editQuizConfig;

	const flowControl = useVerityAssessmentFlow({
		isEditing,
		addInstructions,
		questionDelivery,
		assessmentType,
		assessmentDetails,
		fetchConfig,
		quizQuestions,
		onFinish,
		verityAssessment,
		assessment,
		editQuizConfig,
	});

	const { curStepName, lmsQuizId } = flowControl;

	const handleModuleChange = useCallback(
		async (moduleId: string, courseId?: number) => {
			assessmentDetails.course.selectModule?.(moduleId);

			if (courseId) {
				await fetchCourseModules(courseId);
			}
		},
		[assessmentDetails.course, fetchCourseModules],
	);

	return (
		<div className={styles.verityAssessmentSection}>
			<ScreenReaderText component={"h1"}>{i18n.t(`${baseTransKey}.title`)}</ScreenReaderText>
			<div className={styles.scrollWrapper}>
				<CoreCard style={{ height: loading ? "65px" : undefined }} className={styles.stepperCard}>
					<CoreStepper<VerityAssessmentPageName>
						activeStep={flowControl.step}
						steps={flowControl.stepConfig}
						onStepChange={flowControl.handleGoToStep}
						completedSteps={flowControl.completedSteps}
						loading={loading}
					/>
				</CoreCard>

				{curStepName === "assessmentType" && (
					<AssessmentTypeStep
						onSelect={(type) => setAssessmentType(type)}
						selectedAssessmentType={assessmentType}
						loading={loading}
					/>
				)}
				{curStepName === "assessmentDetails" && (
					<div className={styles.assessmentDetailsStep}>
						{(lms || courses.length > 0) && (
							<CourseGradeBlock
								{...assessmentDetails.course}
								lms={lms}
								courses={courses}
								modules={modules}
								onModuleChange={handleModuleChange}
								fetchingModules={fetchingModules}
								isEditing={isEditing}
								type={assessmentType}
							/>
						)}
						<GeneralInfoBlock {...assessmentDetails.generalInfo} isEditing={isEditing} />
						<TimingBlock {...assessmentDetails.timing} isEditing={isEditing} />
						<SubmissionBlock {...assessmentDetails.submission} assessmentType={assessmentType} isEditing={isEditing} />
					</div>
				)}
				{curStepName === "instructions" && <InstructionStep {...addInstructions} />}
				{curStepName === "review" && (
					<ReviewStep
						assessmentDetails={assessmentDetails}
						assessmentType={assessmentType}
						editQuizData={editQuizConfig.editQuizData}
						onEdit={flowControl.handleOnEdit}
						instructions={addInstructions}
						applications={applications}
						extensions={extensions}
						enforcedProfilesOnly={enforcedProfilesOnly}
						courses={courses}
						modules={modules}
						questions={questions}
					/>
				)}

				{/**Always render EditQuiz because EditQuizWrapper has all the state for saving the quiz */}
				<div style={{ display: curStepName === "editQuiz" ? "contents" : "none" }}>
					<CoreCard className={styles.editQuizCard}>
						<EditAssessmentOptionsPage
							onClose={() => {}}
							standaloneAssessmentEditQuizPageRef={editQuizRef}
							assessment={{ quizId: lmsQuizId, quizInfo: { name: assessmentDetails.generalInfo.assessmentTitle } }}
							quizType={assessmentType === "quiz" ? "verityQuiz" : "verityAssignment"}
							{...flowControl.buttonHandlers}
							{...editQuizWrapperProps}
						/>
					</CoreCard>
				</div>

				{curStepName === "questions" && <QuestionsStep {...quizQuestions} />}
			</div>
			<CreateAssessmentFooter slotProps={flowControl.footerConfig} className={styles.verityAssessmentFooter} />
		</div>
	);
}
